import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { appPropTypes } from '../../../../_shared';

import { FORM_EDITOR_FIELDS } from '../hooks/useFormEditorData';
import { actionsShape, formDataShape } from '../utils/propTypes';

const FormEditorContext = React.createContext();

const propTypes = {

	/**
	 * The form data state.
	 */
	formData: formDataShape.isRequired,

	/**
	 * The default language for the form.
	 */
	defaultLanguage: PropTypes.string.isRequired,

	/**
	 * The name of the organization (required for forms with privacy level in-use).
	 */
	orgName: PropTypes.string,

	/**
	 * True, if the privacy level is in-use for the form.
	 */
	showPrivacy: PropTypes.bool,

	/**
	 * Flag specifying if mobile view should be rendered.
	 */
	mobileView: PropTypes.bool,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack,

	/**
	 * The set of CRUD actions for updating the form data state.
	 */
	actions: actionsShape.isRequired,

	/**
	 * The component children.
	 */
	children: PropTypes.node.isRequired,

};

/**
 * The form editor context provider.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object} The provider component.
 */
const FormEditorProvider = (props) => {

	const {
		formData,
		defaultLanguage,
		orgName,
		showPrivacy,
		mobileView,
		filestack,
		actions,
		children,
	} = props;

	// Keep track of which field was last clicked on
	const [activeFieldID, setActiveFieldID] = useState(null);

	// Get the currently selected language
	const selectedLanguage = formData[FORM_EDITOR_FIELDS.LANGUAGE];

	// Check if editing content in default language for the form
	const isEditingDefaultLanguage = defaultLanguage === selectedLanguage;

	return (
		<FormEditorContext.Provider
			value={{
				formData,
				defaultLanguage,
				selectedLanguage,
				isEditingDefaultLanguage,
				orgName,
				showPrivacy,
				mobileView,
				filestack,
				actions,
				activeFieldID,
				setActiveFieldID,
			}}>
			{children}
		</FormEditorContext.Provider>
	);

};

FormEditorProvider.propTypes = propTypes;

/**
 * The form editor context hook.
 *
 * @returns {Object} -                            The shared context state.
 * @returns {Object} -.formData                   The form data state.
 * @returns {String} -.defaultLanguage            The default language.
 * @returns {String} -.selectedLanguage           The selected language.
 * @returns {Boolean} -.isEditingDefaultLanguage  True, if editing in the default language.
 * @returns {String} -.orgName                    The organization name.
 * @returns {Boolean} -.showPrivacy               True, if privacy fields should be shown.
 * @returns {Boolean} -.mobileView                True, if mobile view should be shown.
 * @returns {Object} -.filestack                  The filestack settings.
 * @returns {Object} -.actions                    CRUD actions related to the form editor.
 * @returns {String} -.activeFieldID              The ID of the active field, if any.
 * @returns {Func} -.setActiveFieldID             Callback function to change the active field.
 */
function useFormEditorContext() {

	const context = useContext(FormEditorContext);

	if (!context) {

		throw new Error('useFormEditorContext must be used within an FormEditorProvider');

	}

	return context;

}

export {
	FormEditorProvider,
	useFormEditorContext,
};
